import { Formik } from 'formik';
import { isEmpty, isEqual } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import type { ModalElement } from '@hivebrite/design-system/Dialog';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';

import { ConfirmModal } from './components/ConfirmModal';
import { NotifyPeople } from './components/NotifyPeople';
import { QuillInput } from './components/QuillInput';
import { SubmitButton } from './components/SubmitButton';
import { TopicTags } from './components/TopicTags';
import { useUserPost } from './hooks/useUserPost';
import { makeHandleSubmit } from './requests/makeHandleSubmit';
import { FeedType } from './types';
import type { UserPostFormModalProps } from './types';
import { getInitialValues } from './utils/getInitialValues';

const UserPostFormModal = ({
  isOpen,
  onCloseModal,
  onCreate,
  onUpdate,
  topic,
  type,
  userPost: initialData,
  userPostId,
}: UserPostFormModalProps) => {
  const { currentUser } = useDefaultAppContexts();

  const [isConfirmActive, setIsConfirmActive] = useState(false);
  const [isEmojiPickerActive, setIsEmojiPickerActive] = useState(false);

  const isEditingPost = !!userPostId;

  const isTopicPost = [
    FeedType.GROUP_FEED_TYPE,
    FeedType.MY_GROUPS_FEED_TYPE,
  ].includes(type);

  const userPost = useUserPost({
    initialData,
    isTopicPost,
    topicId: topic?.id,
    userPostId,
  });

  const isLoading = isEmpty(userPost);

  const closeButtonText = isEditingPost
    ? s__('Accessibility|Close edit a post window')
    : s__('Accessibility|Close create a post window');

  const header = isEditingPost
    ? __('Edit post')
    : s__('LiveFeed|Write a post · %{topicName}').i__({
        topicName: isTopicPost && topic ? topic.name : __('global feed'),
      });

  const footer = (
    <>
      <NotifyPeople
        isEditingPost={isEditingPost}
        isTopicPost={isTopicPost}
        userPost={userPost}
      />
      <SubmitButton isEditingPost={isEditingPost} />
    </>
  );

  const initialValues = getInitialValues({
    isTopicPost,
    topicId: topic?.id,
    userPost,
  });

  const ref = useRef<ModalElement>(null);

  const modalElement = useMemo(() => ref.current?.getModalElement(), [ref]);

  const modalHeaderId = useMemo(
    () => ref.current?.getHeaderElement?.()?.getAttribute('id'),
    [ref],
  );

  const handleSubmit = useMemo(
    () =>
      makeHandleSubmit({
        currentUserId: currentUser?.id,
        isEditingPost,
        isTopicPost,
        onCloseModal,
        onCreate,
        onUpdate,
        topic,
        type,
        userPostId: userPost?.id,
      }),
    [
      currentUser?.id,
      isEditingPost,
      isTopicPost,
      onCloseModal,
      onCreate,
      onUpdate,
      topic,
      type,
      userPost?.id,
    ],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ initialValues, values }) => (
        <>
          <ModalWithTemplate
            enableEscAction={!isEmojiPickerActive}
            footer={footer}
            header={header}
            isLoading={isLoading}
            isOpen={isOpen}
            isSticky
            onCloseModal={() =>
              isEqual(initialValues, values)
                ? onCloseModal()
                : setIsConfirmActive(true)
            }
            pb={0}
            ref={ref}
            screenReadersCloseButtonText={closeButtonText}
          >
            {isLoading || !currentUser ? null : (
              <Box
                alignItems={{ _: 'normal', m: 'flex-start' }}
                display={{ _: 'block', m: 'flex' }}
                width="100%"
              >
                <Box alignItems="center" mb={{ _: 6, m: 0 }}>
                  <AvatarIcon src={currentUser.photo_thumb_url} />
                  <Box display={{ _: 'block', m: 'none' }} font="h3-semibold">
                    {currentUser.name}
                  </Box>
                </Box>

                <Box display="block" width="100%">
                  <QuillInput
                    ariaLabelledBy={modalHeaderId}
                    containerElement={modalElement}
                    initialEmbed={initialValues.post.embed}
                    onToggleEmojiPicker={setIsEmojiPickerActive}
                  />
                  {topic ? (
                    <TopicTags isTopicPost={isTopicPost} topic={topic} />
                  ) : null}
                </Box>

                <ErrorFocus />
              </Box>
            )}
          </ModalWithTemplate>

          <ConfirmModal
            isEditingPost={isEditingPost}
            isOpen={isConfirmActive}
            isTopicPost={isTopicPost}
            onCloseModal={onCloseModal}
            setDisplayConfirmModal={setIsConfirmActive}
            topicId={topic?.id}
            userPostId={userPost?.id}
          />
        </>
      )}
    </Formik>
  );
};

export default UserPostFormModal;

const AvatarIcon = styled(Avatar).attrs(() => {
  const size = { _: 48, m: 60 };

  return {
    height: size,
    minHeight: size,
    minWidth: size,
    mr: 3,
    width: size,
  };
})``;
